import { PartnerWithSitesDTO } from "src/app/api/api-types";
import { deserializeSite, Site } from "src/app/core/sites/site.model";
import { joinPath, UUID } from "src/utils";
import { parseUUID } from "src/utils/converters/model-converters";

export class Partner {
  public constructor(args: ClassProperties<Partner>) {
    this.displayName = args.displayName;
    this.name = args.name;
    this.key = args.key;
    this.status = args.status;
  }

  public readonly displayName: string;
  public readonly name: string;
  public readonly key: UUID;
  public readonly status: string;

  public static parseRouteInfo(url: string): {
    /**
     * The partner key from the URL, if present, or `"_"` if it's the "selected
     * partner" placeholder.
     */
    readonly partnerKey: UUID | "_" | null;
    /** The remaining part of the URL after the partner path. */
    readonly childPath: string;
  } {
    const groups: UrlMatchGroups = url.match(urlPartnerPartMatch)?.groups ?? {};
    return {
      partnerKey: groups.key === "_" ? "_" : parseUUID(groups.key),
      childPath: groups.childPath ?? url,
    };
  }

  /**
   * Replaces the partner portion of the provided URL and returns the new URL for
   * this partner.
   *
   * @param url - The URL to replace the partner path in.
   */
  public replaceRouted(url: string): string {
    return url.replace(
      urlPartnerPartMatch,
      `/partners/${this.key.toString()}$<childPath>`,
    );
  }

  public getRouteUrl(...childPaths: string[]): string {
    return getPartnerRouteUrl(this.key, ...childPaths);
  }
}

export function deserializePartner(
  data: PartnerWithSitesDTO,
): PartnerWithSites {
  const deserializedBasePartner = deserializeBasePartner(data);
  return new PartnerWithSites({
    ...deserializedBasePartner,
    sites: data.sites.map((site) =>
      deserializeSite(site, { partner: deserializedBasePartner }),
    ),
  });
}

export class PartnerWithSites extends Partner {
  public constructor(args: ClassProperties<PartnerWithSites>) {
    super(args);
    this.sites = args.sites;
  }

  public readonly sites: Site[];
}

export function deserializeBasePartner(
  data: Omit<PartnerWithSitesDTO, "sites">,
): Partner {
  return new Partner({
    ...data,
    displayName: data.displayName || data.name,
    key: new UUID(data.partnerKey),
  });
}

const urlPartnerPartMatch =
  /\/partners\/(?<key>_|[a-z0-9-]+)(?<childPath>\/.*|$)/i;
interface UrlMatchGroups {
  readonly key?: string;
  readonly childPath?: string;
}

export function getPartnerRouteUrl(
  partnerKey: Partner["key"],
  ...childPaths: string[]
): string {
  return joinPath("/partners", String(partnerKey), ...childPaths);
}

export function getPartnerApiUrl(
  partnerKey: Partner["key"],
  ...childPaths: string[]
): string {
  return joinPath("/partner", String(partnerKey), ...childPaths);
}
